import React, { useEffect, useState, useRef } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Link, NavLink, useLocation } from "react-router-dom";
import ThemeToggleButton from "../helper/ThemeToggleButton";
import { IMAGE_BASE_URL } from "../Config/Index";
import LoginApi from "../Api/LoginApi";
import OrderApi from "../Api/OrderApi";
import NotifyMeApi from "../Api/NotifyMeApi";
import usePermissions from "../hook/usePermissions";
import { Modal, Button } from "react-bootstrap";

const MasterLayout = ({ children }) => {
  let [sidebarActive, seSidebarActive] = useState(false);
  let [mobileMenu, setMobileMenu] = useState(false);
  const location = useLocation(); // Hook to get the current route
  const sidebarRef = useRef(null);
  const { hasPermission, userType, loading, permissions } = usePermissions();
  const [userData, setUserData] = useState(null);
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  useEffect(() => {
    const fetchUserData = async () => {
      const token = localStorage.getItem("userToken");
      if (!token) return;
      try {
        const result = await LoginApi.getRolesAndPermissions();
        if (result.status) {
          setUserData(result.response.data);
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };
    fetchUserData();
  }, []);

  const [orderCounts, setOrderCounts] = useState({
    all: 0,
    paid: 0,
    unpaid: 0,
    unfulfilled: 0,
    dispatched: 0
  });

  const [notifyCount, setNotifyCount] = useState(0);

  const fetchOrderCounts = async () => {
    try {
      const res = await OrderApi.getOrderCounts();
      if (res.status && res.response) {
        // Handle both cases: res.response.data or res.response
        const counts = res.response.data || res.response;
        setOrderCounts({
          all: counts.All || 0,
          paid: counts.Paid || 0,
          unpaid: counts.Unpaid || 0,
          unfulfilled: counts.Unfulfilled || counts.Unfullied || 0,
          dispatched: counts.Dispatched || 0
        });
      }
    } catch (error) {
      console.error("Error fetching order counts:", error);
    }
  };

  const fetchNotifyCount = async () => {
    try {
      const res = await NotifyMeApi.getNotifyCount();
      if (res.status && res.response) {
        // Accessing res.response.data.count based on the API response structure
        setNotifyCount(res.response.data?.count || 0);
      }
    } catch (error) {
      console.error("Error fetching notify count:", error);
    }
  };

  useEffect(() => {
    if (!loading && (hasPermission("All Orders") || hasPermission("Paid Orders") || hasPermission("Failed Orders") || hasPermission("Unfulfilled Orders") || hasPermission("Unfullied Orders") || hasPermission("Dispatched Orders"))) {
      fetchOrderCounts();
    }
    if (!loading && hasPermission("Notify Me")) {
      fetchNotifyCount();
    }
  }, [loading, permissions]);
  useEffect(() => {
    const handleDropdownClick = (event) => {
      event.preventDefault();
      const clickedLink = event.currentTarget;
      const clickedDropdown = clickedLink.closest(".dropdown");

      if (!clickedDropdown) return;

      const isActive = clickedDropdown.classList.contains("open");
      const allDropdowns = document.querySelectorAll(".sidebar-menu .dropdown");
      allDropdowns.forEach((dropdown) => {
        dropdown.classList.remove("open");
        const submenu = dropdown.querySelector(".sidebar-submenu");
        if (submenu) {
          submenu.style.maxHeight = "0px";
        }
      });
      if (!isActive) {
        clickedDropdown.classList.add("open");
        const submenu = clickedDropdown.querySelector(".sidebar-submenu");
        if (submenu) {
          submenu.style.maxHeight = `${submenu.scrollHeight}px`;
        }
      }
    };
    const dropdownTriggers = document.querySelectorAll(
      ".sidebar-menu .dropdown > a, .sidebar-menu .dropdown > Link",
    );

    dropdownTriggers.forEach((trigger) => {
      trigger.addEventListener("click", handleDropdownClick);
    });

    const openActiveDropdown = () => {
      const allDropdowns = document.querySelectorAll(".sidebar-menu .dropdown");
      allDropdowns.forEach((dropdown) => {
        const submenuLinks = dropdown.querySelectorAll(".sidebar-submenu li a");
        submenuLinks.forEach((link) => {
          const path = link.getAttribute("href") || link.getAttribute("to");
          if (
            (path &&
              (location.pathname === path ||
                location.pathname.startsWith(path + "/"))) ||
            link.classList.contains("active-page")
          ) {
            dropdown.classList.add("open");
            const submenu = dropdown.querySelector(".sidebar-submenu");
            if (submenu) {
              submenu.style.maxHeight = `${submenu.scrollHeight}px`;
            }
          }
        });
      });

      setTimeout(() => {
        const activeLink = document.querySelector(".sidebar-menu .active-page");
        if (activeLink && sidebarRef.current) {
          activeLink.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }
      }, 300);
    };

    openActiveDropdown();
    return () => {
      dropdownTriggers.forEach((trigger) => {
        trigger.removeEventListener("click", handleDropdownClick);
      });
    };
  }, [location.pathname, permissions]);
  useEffect(() => {
    const savedScrollPos = sessionStorage.getItem("sidebarScroll");
    if (savedScrollPos && sidebarRef.current) {
      sidebarRef.current.scrollTop = parseInt(savedScrollPos, 10);
    }

    const handleScroll = () => {
      if (sidebarRef.current) {
        sessionStorage.setItem("sidebarScroll", sidebarRef.current.scrollTop);
      }
    };

    const sidebar = sidebarRef.current;
    if (sidebar) {
      sidebar.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (sidebar) {
        sidebar.removeEventListener("scroll", handleScroll);
      }
    };
  }, []);

  let sidebarControl = () => {
    seSidebarActive(!sidebarActive);
  };

  let mobileMenuControl = () => {
    setMobileMenu(!mobileMenu);
  };

  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const dateStr = new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "long",
    year: "numeric",
    timeZone: "Asia/Kolkata",
  })
    .format(currentTime)
    .replace(/ /g, "-");

  const timeStr = new Intl.DateTimeFormat("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
    timeZone: "Asia/Kolkata",
  }).format(currentTime);

  const formattedTime = `${dateStr} ${timeStr}`;

  const confirmLogout = () => {
    localStorage.clear();
    window.location.href = "/sign-in";
  };

  return (
    <section className={mobileMenu ? "overlay active" : "overlay "}>
      <aside
        className={
          sidebarActive
            ? "sidebar active "
            : mobileMenu
              ? "sidebar sidebar-open"
              : "sidebar"
        }
      >
        <button
          onClick={mobileMenuControl}
          type="button"
          className="sidebar-close-btn"
        >
          <Icon icon="radix-icons:cross-2" />
        </button>
        <div>
          <Link to="/" className="sidebar-logo">
            {sidebarActive ? (
              <img
                src="/assets/images/logo-icon.png"
                alt="Prrayasha Collections"
                className="logo-icon"
                style={{ width: '40px', objectFit: 'contain' }}
              />
            ) : (
              <span className="sidebar-logo-text">
                <span className="logo-word"><span className="logo-highlight">P</span>RRAYASHA</span>
                <span className="logo-word"><span className="logo-highlight">C</span>OLLECTIONS</span>
              </span>
            )}
          </Link>
        </div>
        <style>{`
        /* --- Premium Green Sidebar Design System --- */
        .sidebar-menu-area {
          background-color: #364e3d !important;
        }

        .sidebar-menu li a {
          background-color: transparent; /* Removed !important to allow hover to win */
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
          margin: 4px 12px !important;
          padding: 12px 16px !important;
          border-radius: 10px !important;
          display: flex !important;
          align-items: center !important;
          font-weight: 500 !important;
          text-decoration: none !important;
          font-size: 14px !important;
        }

        /* Default Colors (White on Dark) */
        .sidebar-menu li a,
        .sidebar-menu li a *,
        .sidebar-menu li a::after {
          color: #ffffff !important;
          opacity: 1 !important;
          transition: all 0.2s ease !important;
        }

        /* THE UNIFIED HIGHLIGHT (Hover, Active, Open) - Exact same for all items */
        .sidebar-menu li a:hover,
        .sidebar-menu li a.active-page,
        .sidebar-menu li.dropdown.open > a,
        #sidebar-menu li a:hover,
        #sidebar-menu li a.active-page,
        #sidebar-menu li.dropdown.open > a {
          background-color: #edf1ee !important; /* Light Green Bg */
          color: #364e3d !important;
        }

        /* Enforce dark green on ALL children (Icons, Spans, SVGs) when highlighted */
        .sidebar-menu li a:hover *,
        .sidebar-menu li a.active-page *,
        .sidebar-menu li.dropdown.open > a *,
        .sidebar-menu li a:hover::after,
        .sidebar-menu li a.active-page::after,
        .sidebar-menu li.dropdown.open > a::after {
          color: #364e3d !important;
          fill: #364e3d !important;
          opacity: 1 !important;
        }

        /* Premium Active Submenu Item (Floating) */
        .sidebar-menu .sidebar-submenu li a.active-page {
          background-color: #ffffff !important;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1) !important;
          transform: translateX(4px) !important;
          font-weight: 700 !important;
        }

        /* Dropdown Arrow - CSS Only Chevron (Robust fix for "looking like letters") */
        .sidebar-menu li.dropdown > a::after {
          display: block !important;
          content: "" !important;
          width: 8px !important;
          height: 8px !important;
          border-right: 2px solid currentColor !important;
          border-bottom: 2px solid currentColor !important;
          margin-left: auto !important;
          transform: rotate(-45deg) !important;
          transition: transform 0.3s ease !important;
          margin-top: -4px !important; /* Center alignment */
        }

        .sidebar-menu li.dropdown.open > a::after {
          transform: rotate(45deg) !important;
          margin-top: -6px !important;
        }

        /* Submenu Refinement - Fix for "peeking" background */
        .sidebar-submenu {
          background-color: rgba(0, 0, 0, 0.1) !important;
          margin: 0 !important;
          padding: 0 !important; /* Start with no padding */
          border-radius: 0 0 10px 10px !important;
          overflow: hidden !important;
          max-height: 0;
          transition: max-height 0.3s ease-out, padding 0.3s ease-out !important;
        }

        /* Only apply padding and height when open */
        .sidebar-menu li.dropdown.open .sidebar-submenu {
          max-height: 1000px; /* Large enough for any menu */
          padding: 8px 0 !important;
        }

        .sidebar-menu .sidebar-submenu li a {
          padding: 10px 16px 10px 48px !important;
          margin: 2px 12px !important;
          font-size: 14px !important;
        }

        /* Premium Active Submenu Item (The user's favorite floating design) */
        .sidebar-menu .sidebar-submenu li a.active-page {
          background-color: #ffffff !important;
          color: #364e3d !important;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1) !important;
          transform: translateX(4px) !important;
          font-weight: 700 !important;
        }

        .sidebar-menu .sidebar-submenu li a.active-page::before {
          content: "";
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          width: 4px;
          height: 18px;
          background-color: #364e3d;
          border-radius: 2px;
        }

        /* --- Header Dropdowns Unification --- */
        .dropdown-menu .Link, 
        .dropdown-menu .dropdown-item,
        .dropdown-menu button,
        .dropdown-menu a {
          transition: all 0.2s ease !important;
        }

        .dropdown-menu li button:hover,
        .dropdown-menu li a:hover,
        .dropdown-menu .hover-bg-primary-50:hover {
          background-color: #edf1ee !important;
          color: #364e3d !important;
        }

        .sidebar-menu li a .badge-count {
          background-color: #ffffff !important;
          color: #364e3d !important;
          font-size: 11px;
          font-weight: 700;
          padding: 2px 8px;
          border-radius: 6px;
          margin-left: auto;
          line-height: 1;
          display: inline-flex !important;
          align-items: center;
          justify-content: center;
          min-width: 24px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          transition: all 0.3s ease;
        }
        
        .sidebar-menu li a:hover .badge-count,
        .sidebar-menu li a.active-page .badge-count {
           background-color: #364e3d !important;
           color: #ffffff !important;
           box-shadow: 0 2px 8px rgba(0,0,0,0.2);
        }

        .sidebar-logo {
          border: none !important;
          border-bottom: 1px solid rgba(255, 255, 255, 0.15) !important;
          border-radius: 0 !important;
          padding: 24px 16px !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          background: transparent !important;
          margin-bottom: 10px !important;
        }

        .sidebar-logo-text {
          font-family: 'Aclonica', sans-serif !important;
          font-size: 14px !important; /* Back to original size */
          font-weight: 500 !important;
          color: #ffffff !important;
          letter-spacing: 0.05em !important;
          line-height: 1.2 !important;
          white-space: nowrap !important;
          display: flex !important;
          align-items: center !important;
          gap: 6px !important; /* Proper gap between words */
        }

        .sidebar-logo-text .logo-highlight {
          font-size: 16px !important;
          font-weight: 700 !important;
        }

        .sidebar-logo-text .logo-word {
          display: flex !important;
          align-items: center !important;
        }

        /* Responsive Fix for Small Laptops (1200px - 1400px) only */
        @media screen and (min-width: 1200px) and (max-width: 1400px) {
          .sidebar-logo-text {
            font-size: 11px !important; /* Slightly reduced for better fit */
            gap: 4px !important;
          }
          .sidebar-logo-text .logo-highlight {
            font-size: 11px !important;
          }
          .sidebar-logo {
            padding: 4px 8px !important; 
          }
        }
      `}</style>
        <div className="sidebar-menu-area" ref={sidebarRef}>
          <ul className="sidebar-menu" id="sidebar-menu">
            {hasPermission("Dashboard") && (
              <li>
                <NavLink
                  to="/"
                  className={(navData) =>
                    navData.isActive ? "active-page" : ""
                  }
                >
                  <Icon
                    icon="solar:home-smile-angle-outline"
                    className="menu-icon"
                  />
                  <span>Dashboard</span>
                </NavLink>
              </li>
            )}

            {(hasPermission("Website") ||
              hasPermission("Banner") ||
              hasPermission("Testimonials") ||
              hasPermission("Advertisement") ||
              hasPermission("Youtube")) && (
                <li className="dropdown">
                  <Link to="#" className={location.pathname.startsWith("/banner") ? "active-page" : ""}>
                    <Icon icon="solar:global-outline" className="menu-icon" />
                    <span>Website</span>
                  </Link>
                  <ul className="sidebar-submenu">
                    {hasPermission("Banner") && (
                      <li>
                        <NavLink to="/banner" className={(navData) => navData.isActive ? "active-page" : ""}>
                          Banner
                        </NavLink>
                      </li>
                    )}

                    {/* {(hasPermission("Blog")) && (
                    <li>
                      <NavLink to="/blog" className={(navData) => navData.isActive ? "active-page" : ""}>
                        Blog List
                      </NavLink>
                    </li>
                  )} */}
                    {(hasPermission("Testimonials")) && (
                      <li>
                        <NavLink to="/testimonials" className={(navData) => navData.isActive ? "active-page" : ""}>
                          Testimonials
                        </NavLink>
                      </li>
                    )}
                    {(hasPermission("Advertisement")) && (
                      <li>
                        <NavLink to="/advertisement" className={(navData) => navData.isActive ? "active-page" : ""}>
                          Advertisement
                        </NavLink>
                      </li>
                    )}
                    {(hasPermission("Youtube")) && (
                      <li>
                        <NavLink to="/youtube" className={(navData) => navData.isActive ? "active-page" : ""}>
                          Youtube
                        </NavLink>
                      </li>
                    )}
                  </ul>
                </li>
              )}


            {(hasPermission("Category") ||
              hasPermission("Sub Category") ||
              hasPermission("Child Category") ||
              hasPermission("Brands") ||
              hasPermission("Attributes") ||
              hasPermission("Unit") ||
              hasPermission("Tax") ||
              hasPermission("Products")
              ||
              hasPermission("Shipping Methods")
            ) && (
                <li className="dropdown">
                  <Link to="#"
                    className={
                      location.pathname.startsWith("/category") ||
                        location.pathname.startsWith("/sub-category") ||
                        location.pathname.startsWith("/child-category") ||
                        location.pathname.startsWith("/brands") ||
                        location.pathname.startsWith("/attributes") ||
                        location.pathname.startsWith("/unit") ||
                        location.pathname.startsWith("/tax") ||
                        location.pathname.startsWith("/products") ||
                        location.pathname.startsWith("/coupons")
                        ||
                        location.pathname.startsWith("/shipping-methods")
                        ? "active-page"
                        : ""
                    }
                  >
                    <Icon icon="solar:layers-minimalistic-outline" className="menu-icon" />
                    <span>Catalog</span>
                  </Link>
                  <ul className="sidebar-submenu">
                    {hasPermission("Category") && (
                      <li>
                        <NavLink to="/category" className={(navData) => navData.isActive ? "active-page" : ""}>
                          Category
                        </NavLink>
                      </li>
                    )}
                    {hasPermission("Sub Category") && (
                      <li>
                        <NavLink to="/sub-category" className={(navData) => navData.isActive ? "active-page" : ""}>
                          Sub Category
                        </NavLink>
                      </li>
                    )}
                    {hasPermission("Child Category") && (
                      <li>
                        <NavLink to="/child-category" className={(navData) => navData.isActive ? "active-page" : ""}>
                          Child Category
                        </NavLink>
                      </li>
                    )}
                    {/* {hasPermission("Brands") && (
                      <li>
                        <NavLink to="/brands" className={(navData) => navData.isActive ? "active-page" : ""}>
                          Brands
                        </NavLink>
                      </li>
                    )} */}
                    {hasPermission("Attributes") && (
                      <li>
                        <NavLink to="/attributes" className={(navData) => navData.isActive ? "active-page" : ""}>
                          Attributes
                        </NavLink>
                      </li>
                    )}
                    {/* {hasPermission("Unit") && (
                      <li>
                        <NavLink to="/units" className={(navData) => navData.isActive ? "active-page" : ""}>
                          Unit
                        </NavLink>
                      </li>
                    )} */}
                    {hasPermission("Tax") && (
                      <li>
                        <NavLink to="/taxes" className={(navData) => navData.isActive ? "active-page" : ""}>
                          Taxes
                        </NavLink>
                      </li>
                    )}
                    {hasPermission("Products") && (
                      <li>
                        <NavLink to="/products" className={(navData) => navData.isActive ? "active-page" : ""}>
                          Products
                        </NavLink>
                      </li>
                    )}
                    {hasPermission("Coupons") && (
                      <li>
                        <NavLink to="/coupons" className={(navData) => navData.isActive ? "active-page" : ""}>
                          Coupons
                        </NavLink>
                      </li>
                    )}
                    {hasPermission("Shipping Methods") && (
                      <li>
                        <NavLink to="/shipping-methods" className={(navData) => navData.isActive ? "active-page" : ""}>
                          Shipping Methods
                        </NavLink>
                      </li>
                    )}
                  </ul>
                </li>
              )}

            {(hasPermission("All Orders") ||
              hasPermission("Paid Orders") ||
              hasPermission("Failed Orders") ||
              hasPermission("Unfulfilled Orders") ||
              hasPermission("Unfullied Orders") ||
              hasPermission("Dispatched Orders") ||
              hasPermission("Customer Reviews")) && (
                <li className="dropdown">
                  <Link to="#">
                    <Icon icon="solar:cart-large-4-outline" className="menu-icon" />
                    <span>Orders</span>
                  </Link>
                  <ul className="sidebar-submenu">
                    {hasPermission("All Orders") && (
                      <li>
                        <NavLink to="/all-orders" className={(navData) => navData.isActive ? "active-page" : ""}>
                          All Orders
                          {/* <span className="badge-count">{orderCounts.all}</span> */}
                        </NavLink>
                      </li>
                    )}
                    {hasPermission("Paid Orders") && (
                      <li>
                        <NavLink to="/paid-orders" className={(navData) => navData.isActive ? "active-page" : ""}>
                          Paid Orders <span className="badge-count">{orderCounts.paid}</span>
                        </NavLink>
                      </li>
                    )}
                    {(hasPermission("Unfulfilled Orders") || hasPermission("Unfullied Orders")) && (
                      <li>
                        <NavLink to="/unfulfilled-orders" className={(navData) => navData.isActive ? "active-page" : ""}>
                          Unfulfilled Orders <span className="badge-count">{orderCounts.unfulfilled}</span>
                        </NavLink>
                      </li>
                    )}
                    {hasPermission("Dispatched Orders") && (
                      <li>
                        <NavLink to="/dispatched-orders" className={(navData) => navData.isActive ? "active-page" : ""}>
                          Dispatched Orders <span className="badge-count">{orderCounts.dispatched}</span>
                        </NavLink>
                      </li>
                    )}
                    {hasPermission("Failed Orders") && (
                      <li>
                        <NavLink to="/unpaid-orders" className={(navData) => navData.isActive ? "active-page" : ""}>
                          Failed Orders <span className="badge-count">{orderCounts.unpaid}</span>
                        </NavLink>
                      </li>
                    )}
                    {hasPermission("Customer Reviews") && (
                      <li>
                        <NavLink to="/orders-feedback" className={(navData) => navData.isActive ? "active-page" : ""}>
                          Customer Reviews
                        </NavLink>
                      </li>
                    )}
                  </ul>
                </li>
              )}

            {(hasPermission("Admin Users") ||
              hasPermission("Role & Permission") ||
              hasPermission("Activity Logs") ||
              hasPermission("Customer List")) && (
                <li className="dropdown">
                  <Link to="#" className={
                    location.pathname.startsWith("/admin-registration") ||
                      location.pathname.startsWith("/roles-permissions") ||
                      location.pathname.startsWith("/activity-logs") ||
                      location.pathname.startsWith("/customer-list") ||
                      location.pathname.startsWith("/notify-me") ||
                      location.pathname.startsWith("/contact-us") ? "active-page" : ""
                  }>
                    <Icon icon="solar:users-group-two-rounded-outline" className="menu-icon" />
                    <span>Users</span>
                  </Link>
                  <ul className={
                    location.pathname.startsWith("/admin-registration") ||
                      location.pathname.startsWith("/roles-permissions") ||
                      location.pathname.startsWith("/activity-logs") ||
                      location.pathname.startsWith("/customer-list") ||
                      location.pathname.startsWith("/notify-me") ||
                      location.pathname.startsWith("/contact-us") ? "sidebar-submenu d-block" : "sidebar-submenu"
                  }>
                    {hasPermission("Role & Permission") && (
                      <li>
                        <NavLink to="/roles-permissions" className={(navData) => navData.isActive ? "active-page" : ""}>
                          Role & Permission
                        </NavLink>
                      </li>
                    )}
                    {hasPermission("Admin Users") && (
                      <li>
                        <NavLink to="/admin-registration" className={(navData) => navData.isActive ? "active-page" : ""}>
                          Admin Users
                        </NavLink>
                      </li>
                    )}
                    {hasPermission("Activity Logs") && (
                      <li>
                        <NavLink to="/activity-logs" className={(navData) => navData.isActive ? "active-page" : ""}>
                          Activity Logs
                        </NavLink>
                      </li>
                    )}
                    {hasPermission("Customer List") && (
                      <li>
                        <NavLink to="/customer-list" className={(navData) => navData.isActive ? "active-page" : ""}>
                          Customer List
                        </NavLink>
                      </li>
                    )}
                    {hasPermission("Notify Me") && (
                      <li>
                        <NavLink to="/notify-me" className={(navData) => navData.isActive ? "active-page" : ""}>
                          Notify Me <span className="badge-count">{notifyCount}</span>
                        </NavLink>
                      </li>
                    )}
                    {hasPermission("Contact Us") && (
                      <li>
                        <NavLink to="/contact-us" className={(navData) => navData.isActive ? "active-page" : ""}>
                          Contact Us
                        </NavLink>
                      </li>
                    )}
                  </ul>
                </li>
              )}

            {/* {(hasPermission("POS Order") || hasPermission("POS Order History")) && (
              <li className="dropdown">
                <Link to="#">
                  <Icon icon="solar:calculator-outline" className="menu-icon" />
                  <span>POS</span>
                </Link>
                <ul className="sidebar-submenu">
                  {hasPermission("POS Order") && (
                    <li>
                      <NavLink to="/pos-order" className={(navData) => navData.isActive ? "active-page" : ""}>
                          POS Order
                      </NavLink>
                    </li>
                  )}
                  {hasPermission("POS Order History") && (
                    <li>
                      <NavLink to="/pos-history" className={(navData) => navData.isActive ? "active-page" : ""}>
                          POS History
                      </NavLink>
                    </li>
                  )}
                </ul>
              </li>
            )} */}

            {/* {(hasPermission("Vendor") ||
              hasPermission("Purchase Entry")) && (
                <li className="dropdown">
                  <Link to="#">
                    <Icon icon="solar:shop-2-outline" className="menu-icon" />
                    <span>Vendor</span>
                  </Link>
                  <ul className="sidebar-submenu">
                    {hasPermission("Vendor") && (
                      <li>
                        <NavLink to="/vendor-list" className={(navData) => navData.isActive ? "active-page" : ""}>
                          Vendor List
                        </NavLink>
                      </li>
                    )}
                    {hasPermission("Purchase Entry") && (
                      <li>
                        <NavLink to="/purchase-entry" className={(navData) => navData.isActive ? "active-page" : ""}>
                          Purchase Entry
                        </NavLink>
                      </li>
                    )}
                    {hasPermission("Vendor") && (
                      <li>
                        <NavLink to="/vendor-payment" className={(navData) => navData.isActive ? "active-page" : ""}>
                          Payment
                        </NavLink>
                      </li>
                    )}
                  </ul>
                </li>
              )} */}

            {(hasPermission("Payment History") || hasPermission("Manual Payment")) && (
              <li className="dropdown">
                <Link to="#">
                  <Icon icon="solar:card-2-outline" className="menu-icon" />
                  <span>Payments</span>
                </Link>
                <ul className="sidebar-submenu">
                  {hasPermission("Payment History") && (
                    <li>
                      <NavLink to="/payment-history" className={(navData) => navData.isActive ? "active-page" : ""}>
                        Payment History
                      </NavLink>
                    </li>
                  )}
                  {/* {hasPermission("Manual Payment") && (
                    <li>
                      <NavLink to="/manual-payment" className={(navData) => navData.isActive ? "active-page" : ""}>
                        Manual Payment
                      </NavLink>
                    </li>
                  )} */}
                </ul>
              </li>
            )}

            {(hasPermission("Inventory List") || hasPermission("Add Stock")) && (
              <li className="dropdown">
                <Link to="#">
                  <Icon icon="solar:box-minimalistic-outline" className="menu-icon" />
                  <span>Inventory</span>
                </Link>
                <ul className="sidebar-submenu">
                  {hasPermission("Inventory List") && (
                    <li>
                      <NavLink to="/inventory-list" className={(navData) => navData.isActive ? "active-page" : ""}>
                        Inventory List
                      </NavLink>
                    </li>
                  )}
                  {hasPermission("Add Stock") && (
                    <li>
                      <NavLink to="/add-stock" className={(navData) => navData.isActive ? "active-page" : ""}>
                        Add Stock
                      </NavLink>
                    </li>
                  )}
                </ul>
              </li>
            )}

            {(hasPermission("Product Reports") ||
              hasPermission("Customer Reports") ||
              hasPermission("Vendor Reports") ||
              hasPermission("Payment Reports") ||
              hasPermission("Stock Reports") ||
              hasPermission("Sales Reports")) && (
                <li className="dropdown">
                  <Link to="#">
                    <Icon icon="solar:chart-2-outline" className="menu-icon" />
                    <span>Reports</span>
                  </Link>
                  <ul className="sidebar-submenu">
                    {/* {hasPermission("Product Reports") && (
                      <li>
                        <NavLink to="/product-reports" className={(navData) => navData.isActive ? "active-page" : ""}>
                          Product reports
                        </NavLink>
                      </li>
                    )} */}
                    {hasPermission("Customer Reports") && (
                      <li>
                        <NavLink to="/customer-reports" className={(navData) => navData.isActive ? "active-page" : ""}>
                          Customer Reports
                        </NavLink>
                      </li>
                    )}
                    {/* {hasPermission("Vendor Reports") && (
                      <li>
                        <NavLink to="/vendor-reports" className={(navData) => navData.isActive ? "active-page" : ""}>
                          Vendor reports
                        </NavLink>
                      </li>
                    )} */}
                    {hasPermission("Payment Reports") && (
                      <li>
                        <NavLink to="/payment-reports" className={(navData) => navData.isActive ? "active-page" : ""}>
                          Payment Reports
                        </NavLink>
                      </li>
                    )}
                    {hasPermission("Stock Reports") && (
                      <li>
                        <NavLink to="/stock-reports" className={(navData) => navData.isActive ? "active-page" : ""}>
                          Stock Reports
                        </NavLink>
                      </li>
                    )}
                    {/* {hasPermission("Sales Reports") && (
                      <li>
                        <NavLink to="/sales-reports" className={(navData) => navData.isActive ? "active-page" : ""}>
                          Sales reports
                        </NavLink>
                      </li>
                    )} */}
                  </ul>
                </li>
              )}

          </ul>
        </div>
      </aside>

      <main
        className={sidebarActive ? "dashboard-main active" : "dashboard-main"}
      >
        <div className="navbar-header">
          <div className="row align-items-center justify-content-between">
            <div className="col-auto">
              <div className="d-flex flex-wrap align-items-center gap-4">
                <button
                  type="button"
                  className="sidebar-toggle"
                  onClick={sidebarControl}
                >
                  {sidebarActive ? (
                    <Icon
                      icon="iconoir:arrow-right"
                      className="icon text-2xl non-active"
                    />
                  ) : (
                    <Icon
                      icon="heroicons:bars-3-solid"
                      className="icon text-2xl non-active "
                    />
                  )}
                </button>
                <button
                  onClick={mobileMenuControl}
                  type="button"
                  className="sidebar-mobile-toggle"
                >
                  <Icon icon="heroicons:bars-3-solid" className="icon" />
                </button>
              </div>
            </div>
            <div className="col-auto">
              <div className="d-flex flex-wrap align-items-center gap-3">
                <span
                  style={{
                    color: "#364e3d",
                    fontWeight: "600",
                    fontSize: "16px",
                  }}
                >
                  {formattedTime}
                </span>
                {/* <ThemeToggleButton /> */}
                <div className="dropdown d-none d-sm-inline-block">
                  {/* <button
                    className="has-indicator w-40-px h-40-px bg-neutral-200 rounded-circle d-flex justify-content-center align-items-center"
                    type="button"
                    data-bs-toggle="dropdown"
                  >
                    <Icon
                      icon="iconoir:bell"
                      className="text-primary-light text-xl"
                    />
                  </button> */}
                  <div className="dropdown-menu to-top dropdown-menu-lg p-0">
                    <div className="m-16 py-12 px-16 radius-8 bg-primary-50 mb-16 d-flex align-items-center justify-content-between gap-2">
                      <div>
                        <h6 className="text-lg text-primary-light fw-semibold mb-0">
                          Notifications
                        </h6>
                      </div>
                      <span className="text-primary-600 fw-semibold text-lg w-40-px h-40-px rounded-circle bg-base d-flex justify-content-center align-items-center">
                        05
                      </span>
                    </div>
                    <div className="max-h-400-px overflow-y-auto scroll-sm pe-4">
                      <Link
                        to="#"
                        className="px-24 py-12 d-flex align-items-start gap-3 mb-2 justify-content-between"
                      >
                        <div className="text-black hover-bg-transparent hover-text-primary d-flex align-items-center gap-3">
                          <span className="w-44-px h-44-px bg-success-subtle text-success-main rounded-circle d-flex justify-content-center align-items-center flex-shrink-0">
                            <Icon
                              icon="bitcoin-icons:verify-outline"
                              className="icon text-xxl"
                            />
                          </span>
                          <div>
                            <h6 className="text-md fw-semibold mb-4">
                              Congratulations
                            </h6>
                            <p className="mb-0 text-sm text-secondary-light text-w-200-px">
                              Your profile has been Verified. Your profile has
                              been Verified
                            </p>
                          </div>
                        </div>
                        <span className="text-sm text-secondary-light flex-shrink-0">
                          23 Mins ago
                        </span>
                      </Link>
                      <Link
                        to="#"
                        className="px-24 py-12 d-flex align-items-start gap-3 mb-2 justify-content-between bg-neutral-50"
                      >
                        <div className="text-black hover-bg-transparent hover-text-primary d-flex align-items-center gap-3">
                          <span className="w-44-px h-44-px bg-success-subtle text-success-main rounded-circle d-flex justify-content-center align-items-center flex-shrink-0">
                            <img
                              src="/assets/images/notification/profile-1.png"
                              alt=""
                            />
                          </span>
                          <div>
                            <h6 className="text-md fw-semibold mb-4">
                              Ronald Richards
                            </h6>
                            <p className="mb-0 text-sm text-secondary-light text-w-200-px">
                              You can stitch between artboards
                            </p>
                          </div>
                        </div>
                        <span className="text-sm text-secondary-light flex-shrink-0">
                          23 Mins ago
                        </span>
                      </Link>
                      <Link
                        to="#"
                        className="px-24 py-12 d-flex align-items-start gap-3 mb-2 justify-content-between"
                      >
                        <div className="text-black hover-bg-transparent hover-text-primary d-flex align-items-center gap-3">
                          <span className="w-44-px h-44-px bg-info-subtle text-info-main rounded-circle d-flex justify-content-center align-items-center flex-shrink-0">
                            AM
                          </span>
                          <div>
                            <h6 className="text-md fw-semibold mb-4">
                              Arlene McCoy
                            </h6>
                            <p className="mb-0 text-sm text-secondary-light text-w-200-px">
                              Invite you to prototyping
                            </p>
                          </div>
                        </div>
                        <span className="text-sm text-secondary-light flex-shrink-0">
                          23 Mins ago
                        </span>
                      </Link>
                      <Link
                        to="#"
                        className="px-24 py-12 d-flex align-items-start gap-3 mb-2 justify-content-between bg-neutral-50"
                      >
                        <div className="text-black hover-bg-transparent hover-text-primary d-flex align-items-center gap-3">
                          <span className="w-44-px h-44-px bg-success-subtle text-success-main rounded-circle d-flex justify-content-center align-items-center flex-shrink-0">
                            <img
                              src="/assets/images/notification/profile-2.png"
                              alt=""
                            />
                          </span>
                          <div>
                            <h6 className="text-md fw-semibold mb-4">
                              Annette Black
                            </h6>
                            <p className="mb-0 text-sm text-secondary-light text-w-200-px">
                              Invite you to prototyping
                            </p>
                          </div>
                        </div>
                        <span className="text-sm text-secondary-light flex-shrink-0">
                          23 Mins ago
                        </span>
                      </Link>
                      <Link
                        to="#"
                        className="px-24 py-12 d-flex align-items-start gap-3 mb-2 justify-content-between"
                      >
                        <div className="text-black hover-bg-transparent hover-text-primary d-flex align-items-center gap-3">
                          <span className="w-44-px h-44-px bg-info-subtle text-info-main rounded-circle d-flex justify-content-center align-items-center flex-shrink-0">
                            DR
                          </span>
                          <div>
                            <h6 className="text-md fw-semibold mb-4">
                              Darlene Robertson
                            </h6>
                            <p className="mb-0 text-sm text-secondary-light text-w-200-px">
                              Invite you to prototyping
                            </p>
                          </div>
                        </div>
                        <span className="text-sm text-secondary-light flex-shrink-0">
                          23 Mins ago
                        </span>
                      </Link>
                    </div>
                    <div className="text-center py-12 px-16">
                      <Link
                        to="#"
                        className="text-primary-600 fw-semibold text-md hover-text-primary d-flex justify-content-center align-items-center gap-2"
                      >
                        See All Notifications
                        <Icon
                          icon="solar:arrow-right-linear"
                          className="icon"
                        />
                      </Link>
                    </div>
                  </div>
                </div>
                {/* Notification dropdown end */}

                <div className="dropdown">
                  <button
                    className="d-flex justify-content-center align-items-center rounded-circle border-0 p-0"
                    type="button"
                    data-bs-toggle="dropdown"
                  >
                    {userData?.profileImage ? (
                      <img
                        src={`${IMAGE_BASE_URL}/${userData.profileImage?.path || userData.profileImage}`}
                        alt="image"
                        className="w-40-px h-40-px object-fit-cover rounded-circle"
                      />
                    ) : (
                      <div
                        className="w-40-px h-40-px bg-primary-600 text-white rounded-circle d-flex justify-content-center align-items-center fw-bold"
                        style={{ fontSize: '18px' }}
                      >
                        {userData?.name?.charAt(0).toUpperCase() || "A"}
                      </div>
                    )}
                  </button>
                  <div className="dropdown-menu to-top dropdown-menu-sm p-0">
                    <div className="p-16 px-24 border-bottom">
                      <h6 className="text-lg fw-semibold text-primary-light mb-0">
                        {userData?.name || "Administrator"}
                      </h6>
                      <span className="text-secondary-light text-sm">
                        {userData?.roleId?.name || userData?.role?.name || "Admin"}
                      </span>
                    </div>
                    <ul className="p-16">
                      {/* <li>
                        <Link
                          to="/my-profile"
                          className="d-flex align-items-center gap-3 hover-bg-primary-50 text-secondary-light radius-8 px-12 py-12"
                        >
                          <Icon
                            icon="solar:user-circle-outline"
                            className="icon text-xl"
                          />
                          My Profile
                        </Link>
                      </li> */}
                      <li>
                        <button
                          onClick={() => setShowLogoutModal(true)}
                          className="d-flex align-items-center gap-3 hover-bg-primary-50 text-secondary-light radius-8 px-12 py-12 border-0 bg-transparent w-100"
                        >
                          <Icon
                            icon="solar:logout-2-outline"
                            className="icon text-xl"
                          />
                          Log Out
                        </button>
                      </li>
                    </ul>
                  </div>
                </div>
                {/* Profile dropdown end */}
              </div>
            </div>
          </div>
        </div>

        {/* dashboard-main-body */}
        <div className="dashboard-main-body">{children}</div>

        {/* Footer section */}
        <footer className="d-footer">
          <div className="row align-items-center">
            <div className="col-12 d-flex justify-content-between">

              {/* Left Side */}
              <p className="mb-0 text-start">
                © {new Date().getFullYear()}{" "}
                <span className="text-primary-600">Prrayasha.</span> All Rights Reserved.
              </p>

              {/* Right Side */}
              <p className="mb-0 text-end">
                Developed & Maintained By{" "}
                <span className="text-primary-600">Ocean Softwares</span>
              </p>

            </div>
          </div>
        </footer>
      </main>

      {/* Logout Confirmation Modal */}
      <Modal show={showLogoutModal} onHide={() => setShowLogoutModal(false)} centered>
        <Modal.Body className="text-center p-40">
          <div className="d-flex justify-content-center mb-16">
            <div className="bg-danger-focus rounded-circle d-flex justify-content-center align-items-center w-64-px h-64-px">
              <Icon icon="solar:logout-2-outline" className="text-danger-600 text-xxl" />
            </div>
          </div>
          <h5 className="mb-8">Confirm Logout</h5>
          <p className="text-secondary-light mb-32 text-md">
            Are you sure you want to log out of your account? You will need to sign in again to access the dashboard.
          </p>
          <div className="d-flex justify-content-center gap-3">
            <Button variant="outline-secondary" className="px-32 py-12 radius-8 fw-semibold" onClick={() => setShowLogoutModal(false)}>Cancel</Button>
            <Button variant="danger" className="px-32 py-12 radius-8 fw-semibold" onClick={confirmLogout} style={{ backgroundColor: "#d93939", borderColor: "#d93939" }}>Logout</Button>
          </div>
        </Modal.Body>
      </Modal>
    </section>
  );
};

export default MasterLayout;

